import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  ScrollArea,
  rem,
  Text,
  UnstyledButton,
  Group,
  Center,
  Select,
  Button,
  Checkbox,
  Pagination,
  Loader,
} from "@mantine/core";
import {
  IconSelector,
  IconChevronDown,
  IconChevronUp,
  IconTrash,
  IconEdit,
  IconBellPlus,
  IconEye,
} from "@tabler/icons-react";
import { showErrorAlert, showSuccessAlert } from "../../helper/alerts";
import DeleteModal from "./DeleteModal";
import AddNotificationModal from "./AddNotificationModal";
import EditNotificationModal from "./EditNotificationModal";
import ViewNotificationModal from "./ViewNotificationModal";
import classes from "../Notification/notification.module.css";
import { RowDataNotification } from "./types";
import { useFetchRowData } from "../hooks/useFetchRowData";
import { notification } from "../types/RowDataTypes";
import { CustomTable } from "../Table/CustomTable";
import { SearchBar } from "../SearchBar/SearchBar";
import useAxiosPrivate from "../../auth/useAxiosPrivate";
import Header from "../static/Header";
import getNotificationTable from "../../DataDisplay/DataDisplayTable/NotificationTable";

// Searching and Sorting
enum ColumnHeaderToFilterPair {
//   ID = "Id",
  TITLE = "Title",
  BODY = "Body",
//   TYPE = "Type",
  ROUTE = "Route",
  SEND = "Send",
}

const colFilter = ColumnHeaderToFilterPair;
const colFilters: { [key in ColumnHeaderToFilterPair]: string } = {
//   [colFilter.ID]: "id",
  [colFilter.TITLE]: "title",
  [colFilter.BODY]: "body",
//   [colFilter.TYPE]: "type",
  [colFilter.ROUTE]: "route",
  [colFilter.SEND]: "",
};

interface ThProps {
  children: React.ReactNode;
  sortable: boolean;
  reversed: boolean;
  sorted: boolean;
  onSort(): void;
}

function Th({ children, sortable, reversed, sorted, onSort }: ThProps) {
  const Icon = sorted
    ? reversed
      ? IconChevronUp
      : IconChevronDown
    : IconSelector;

  const allowedSorts = [
    colFilters[colFilter.TITLE],
    colFilters[colFilter.BODY],
    colFilters[colFilter.ROUTE],
  ];
//   console.log(children)
  return (
    <Table.Th className={`${classes.th} ${sortable ? classes.sortable : ""}`}>
      {allowedSorts.includes(children!!.toString().toLowerCase()) ? (
        <UnstyledButton onClick={onSort} className={classes.control}>
          <Group justify="space-between">
            <Text className={classes.Text}>{children}</Text>
            <Center className={classes.icon}>
              <Icon style={{ width: rem(16), height: rem(16) }} stroke={1.5} />
            </Center>
          </Group>
        </UnstyledButton>
      ) : (
        <Text fw={500} fz="sm">
          {children}
        </Text>
      )}
    </Table.Th>
  );
}

const initialData: RowDataNotification[] = [
  {
    id: "",
    title: "",
    body: "",
    type: "",
    route: "",
    send: false,
    action: "",
  },
];

export function Notification() {
  const axiosPrivate = useAxiosPrivate();

  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<keyof RowDataNotification | null>(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  //Used in searching and when deleting to cause rerender to fetch data
  const [tableStateChanged, setTableStateChanged] = useState(0);

  const changeTableState = () => {
    setTableStateChanged(Math.random() * Math.random());
  };

  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  function resetPage() {
    if (currentPage !== 1) {
      setCurrentPage(1);
      return true;
    }
    return false;
  }

  const checkboxesFilter: JSX.Element[] = [];

  //Set allowed search filter checkbox to be shown
  const allowedFilters = [
    // colFilters[colFilter.ID],
    colFilters[colFilter.TITLE],
    colFilters[colFilter.BODY],
    colFilters[colFilter.ROUTE],
  ];
  for (const [key, value] of Object.entries(colFilters)) {
    if (allowedFilters.includes(value)) {
      const checkboxFilter: JSX.Element = (
        <Checkbox value={value} label={key} />
      );
      checkboxesFilter.push(checkboxFilter);
    }
  }

  const [showFilterOptions, setShowFilterOptions] = useState(false);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    setSearch(input);
  };

  const sendAction = (row: RowDataNotification) => async (e: any) => {

    const btn = document.getElementById(
      `sendBtn-${row.id}`
    ) as HTMLButtonElement;
    btn.disabled = true;
    const loader = document.getElementById(`loader-${row.id}`);
    loader!!.style.display = "block";
    const sendText = document.getElementById(`sendTxt-${row.id}`);
    sendText!!.style.display = "none";

    await axiosPrivate
      .post("/api/admin/notifications/send", {
        notification_id: row.id,
      })
      .then((response) => {
        showSuccessAlert(`Notification Sent\n${row.title}`);
        sendText!!.textContent = "Sent"
      })
      .catch((err) => {
        showErrorAlert("An error occurred");
        sendText!!.textContent = "Send"
      });

    loader!!.style.display = "none";
    sendText!!.style.display = "block";
    e.disabled = false;
    btn.disabled = false;
  };

  const fetchedData = useFetchRowData(
    itemsPerPage,
    currentPage,
    search,
    selectedFilters,
    tableStateChanged,
    sortBy ?? "createdAt",
    reverseSortDirection ? "DESC" : "ASC",
    notification,
    getNotificationTable().create()
  );
  const itemsOnPage = useRef(0);
  const [totalRows, setTotalRows] = useState<number>(0);
  // const totalRows = fetchedData.totalRows;
  let rowData: RowDataNotification[] | null =
    fetchedData.data as RowDataNotification[];
  const isLoading: boolean = fetchedData.isLoading;
  const [rowDataNotification, setRowDataNotification] = useState<
    RowDataNotification[] | null
  >(null);

  const [isAddNotificationModalOpen, setAddNotificationModalOpen] =
    useState(false);
  const [isEditNotificationModalOpen, setEditNotificationModalOpen] =
    useState(false);
  const [selectedRowForEdit, setSelectedRowForEdit] =
    useState<RowDataNotification | null>(null);
  const [isViewNotificationModalOpen, setViewNotificationModalOpen] =
    useState(false);
  const [selectedRowForView, setSelectedRowForView] =
    useState<RowDataNotification | null>(null);

  const [isDeleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [selectedRowToDelete, setSelectedRowToDelete] =
    useState<RowDataNotification | null>(null);

  const handleAddNotification = () => {
    setAddNotificationModalOpen(true);
  };

  const handleAddNotificationModalClose = () => {
    setAddNotificationModalOpen(false);
  };

  const handleEdit = (row: RowDataNotification) => {
    setSelectedRowForEdit(row);
    setEditNotificationModalOpen(true);
  };

  const handleAddNotificationSuccess = (
    newNotificationData: RowDataNotification
  ) => {
    // console.log("being added");
    // console.log(newNotificationData);
    axiosPrivate
      .post(
        "/api/admin/notifications/add",
        {
          title: newNotificationData.title,
          body: newNotificationData.body,
          type: newNotificationData.type,
          route: newNotificationData.route,
        },
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      )
      .then((response) => {
        // console.log(response);

        if (itemsOnPage.current < itemsPerPage) {
          const data = response.data;
          const returnedData: RowDataNotification = {
            ...newNotificationData,
            id: data.notifId,
          };
          if (!reverseSortDirection)
            setRowDataNotification((prevData) => [returnedData, ...prevData!!]);
          else
            setRowDataNotification((prevData) => [...prevData!!, returnedData]);
        }
        itemsOnPage.current = itemsOnPage.current + 1;
        setTotalRows((prevData) => prevData + 1);
        showSuccessAlert("Feed Successfully Added");
      })
      .catch((error) => {
        // console.log("error " + error);
        showErrorAlert("Something Went Wrong");
      });
  };

  const handleEditNotificationSuccess = (
    editedNotificationData: RowDataNotification
  ) => {
    axiosPrivate
      .post("/api/admin/notifications/update", {
        notification_id: editedNotificationData.id,
        title: editedNotificationData.title,
        body: editedNotificationData.body,
        type: editedNotificationData.type,
        route: editedNotificationData.route,
      })
      .then((response) => {
        // console.log("edited data");
        // console.log(response);
        // Find the index of the selectedRow in the sortedData
        const indexOfSelectedRow = rowDataNotification!!.findIndex(
          (row) => row === selectedRowForEdit
        );

        // Create a copy of the sortedData
        const updatedData = [...rowDataNotification!!];
        const data = response.data.links;
        // Update the selectedRow in the copied data
        updatedData[indexOfSelectedRow] = {
          ...selectedRowForEdit!!,
          title: editedNotificationData.title,
          body: editedNotificationData.body,
          type: editedNotificationData.type,
          route: editedNotificationData.route,
        };
        // changeTableState();
        setRowDataNotification(updatedData);
        showSuccessAlert("Notification Successfully Edited");
      })
      .catch((error) => {
        // console.log("error " + error);
        showErrorAlert("Something Went Wrong");
      });
    setEditNotificationModalOpen(false);
  };

  const handleDelete = (row: RowDataNotification) => {
    setSelectedRowToDelete(row);
    setDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmed = () => {
    axiosPrivate
      .post("/api/admin/notifications/delete", {
        notification_id: selectedRowToDelete!!.id,
      })
      .then((response) => {
        setDeleteConfirmationOpen(false);
        if (
          itemsOnPage.current == 1 &&
          currentPage !== 1 &&
          currentPage == Math.ceil(totalRows / itemsPerPage)
        )
          setCurrentPage((prev) => prev - 1);
        else changeTableState();
      })
      .catch((err) => {
        showErrorAlert("An error has occured");
      });

    setDeleteConfirmationOpen(false);
    setSelectedRowToDelete(null);
  };

  const handleDeleteCancelled = () => {
    setDeleteConfirmationOpen(false);
    setSelectedRowToDelete(null);
  };

  const handleView = (row: RowDataNotification) => {
    setSelectedRowForView(row);
    setViewNotificationModalOpen(true);
  };

  const setSorting = (field: keyof RowDataNotification) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    changeTableState();
  };

  const startIndex = (currentPage - 1) * itemsPerPage;

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (value: number) => {
    setCurrentPage(1);
    setItemsPerPage(value);
  };

  useEffect(() => {
    setRowDataNotification(rowData);
    itemsOnPage.current = fetchedData.itemsOnPage;
    setTotalRows(fetchedData.totalRows);
  }, [rowData]);

  const rowsToMap = rowDataNotification ?? [];
  const rows = rowsToMap.map((row, index) => (
    <Table.Tr key={index}>
      <Table.Td>{startIndex + index + 1}</Table.Td>
      {/* <Table.Td className={classes.idColumn}>{row.id}</Table.Td> */}
      <Table.Td>{row.title}</Table.Td>
      <Table.Td>{row.body}</Table.Td>
      <Table.Td>{row.route}</Table.Td>
      <Table.Td>
        <Button
          className={classes.sendButton}
          onClick={sendAction(row)}
          id={`sendBtn-${row.id}`}
        >
          <span id={`sendTxt-${row.id}`}>Send</span>
          <Loader
            id={`loader-${row.id}`}
            name="loader"
            style={{ display: "none" }}
            color="white"
          />
        </Button>
      </Table.Td>
      <Table.Td className={classes.actionButtons}>
        <button onClick={() => handleView(row)} className={classes.viewButton}>
          <IconEye /> View
        </button>

        <button onClick={() => handleEdit(row)} className={classes.editbutton}>
          <IconEdit /> Edit
        </button>

        <button
          onClick={() => handleDelete(row)}
          className={classes.trashbutton}
        >
          <IconTrash /> Delete
        </button>
      </Table.Td>
    </Table.Tr>
  ));

  const handleSelectPage = (selectedPage: number) => {
    if (selectedPage != currentPage) setCurrentPage(selectedPage);
  };

  return (
    <div className={classes.notificationPageContainer}>
      <ScrollArea className={classes.parentContainer}>
        <Header title="Notification" />
        <div className={classes.searchAndAddContainer}>
          <SearchBar
            isLoading={isLoading}
            resetPage={resetPage}
            changeTableState={changeTableState}
            search={search}
            handleSearch={handleSearch}
            setShowFilterOptions={setShowFilterOptions}
            showFilterOptions={showFilterOptions}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            checkboxesFilter={checkboxesFilter}
          />
          <div className={classes.AddButtonContainer}>
            <Button
              className={classes.addButton}
              onClick={handleAddNotification}
            >
              <IconBellPlus className={classes.IconBellPlus} />
              Create Notification
            </Button>
          </div>
        </div>

        <ViewNotificationModal
          isOpen={isViewNotificationModalOpen}
          onClose={() => setViewNotificationModalOpen(false)}
          notificationData={selectedRowForView || initialData[0]}
        />

        <AddNotificationModal
          isOpen={isAddNotificationModalOpen}
          onClose={handleAddNotificationModalClose}
          onAddNotification={handleAddNotificationSuccess}
        />

        <EditNotificationModal
          isOpen={isEditNotificationModalOpen}
          onClose={() => setEditNotificationModalOpen(false)}
          onEditNotification={handleEditNotificationSuccess}
          notificationData={selectedRowForEdit || initialData[0]}
        />

        <DeleteModal
          isOpen={isDeleteConfirmationOpen}
          onClose={handleDeleteCancelled}
          onConfirm={handleDeleteConfirmed}
        />

        <ViewNotificationModal
          isOpen={isViewNotificationModalOpen}
          onClose={() => setViewNotificationModalOpen(false)}
          notificationData={selectedRowForView || initialData[0]}
        />

        <CustomTable
          colHeaders={Object.values(ColumnHeaderToFilterPair)}
          filters={Object.values(colFilters)}
          reverseSortDirection={reverseSortDirection}
          sortBy={sortBy}
          setSorting={setSorting}
          customclasses={classes}
          Th={Th}
          rowData={rowDataNotification}
          rows={rows}
        />

        <div className={classes.paginationContainer}>
          <div className={classes.resultCount}>
            <Text>
              {`Showing `}
              <span style={{ fontWeight: "bold" }}>{startIndex + 1}</span>
              {`-`}
              <span style={{ fontWeight: "bold" }}>
                {Math.min(currentPage * itemsPerPage, totalRows)}
              </span>
              {` of `}
              <span style={{ fontWeight: "bold" }}>{totalRows}</span>
              {` results`}
            </Text>
          </div>

          <div className={classes.rightContainer}>
            <div className={classes.rowsPerPageSelector}>
              <Select
                name="loader"
                placeholder="Rows per page"
                data={["10", "20", "50"]}
                value={itemsPerPage.toString()}
                onChange={(value) =>
                  handleRowsPerPageChange(parseInt(value as string, 10))
                }
              />
            </div>

            <div className={classes.paginationSelector}>
              <Pagination
                color="blueviolet"
                value={currentPage}
                total={Math.ceil(totalRows / itemsPerPage)}
                onNextPage={() => handlePageChange(currentPage + 1)}
                onPreviousPage={() => handlePageChange(currentPage - 1)}
                onChange={(value: number) => handleSelectPage(value)}
              />
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}

export default Notification;
